/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Divider, Grid, IconButton } from "@mui/material";
import { PartnerModel, TechnicianModel } from "../../Utils/Models";
import { Add, Delete, Save } from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useEffect, useState } from "react";
import { TextValidator } from "react-material-ui-form-validator";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { deleteData } from "../../Services/deleteData";
import { urlEnum } from "../../Utils/UrlEnum";
import { toast } from "react-toastify";

type TechniciansProp = {
  customer: PartnerModel;
  handleChangeCustomer: (customer: PartnerModel) => void;
};

const Technicians = (prop: TechniciansProp) => {
  const { customer, handleChangeCustomer } = prop;
  const [technicians, setTechnicians] = useState<TechnicianModel[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (customer._id) {
      loadTechnicians();
    }
  }, [customer._id]);

  const loadTechnicians = async () => {
    if (!customer._id) return;

    setLoading(true);
    try {
      const response = await getData(
        `${urlEnum.technicians}/partner/${customer._id}`
      );
      if (response.data.result) {
        setTechnicians(response.data.result);
        // Update the customer's technicians array with the IDs
        const technicianIds = response.data.result.map(
          (t: TechnicianModel) => t._id
        );
        handleChangeCustomer({
          ...customer,
          technicians: technicianIds,
        });
      }
    } catch (error) {
      console.error("Error loading technicians:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTechnician = () => {
    const newTechnician = new TechnicianModel();
    newTechnician.partner = customer._id || null;
    setTechnicians([...technicians, newTechnician]);
  };

  const handleSaveTechnician = async (index: number) => {
    const technician = technicians[index];

    if (!technician.name) {
      toast.error(Vocabulary.requiredField);
      return;
    }

    try {
      if (technician._id) {
        // Update existing technician
        const response = await updateData(
          `${urlEnum.technicians}/${technician._id}`,
          technician
        );
        if (response.data.result) {
          toast.success(Vocabulary.updateSuccess);
          loadTechnicians();
        }
      } else {
        // Create new technician
        const technicianData = {
          ...technician,
          partner: customer._id,
        };
        const response = await postData(urlEnum.technicians, technicianData);
        if (response.data.result) {
          toast.success(Vocabulary.addSuccess);
          loadTechnicians();
        }
      }
    } catch (error) {
      toast.error(Vocabulary.error);
      console.error("Error saving technician:", error);
    }
  };

  const handleRemoveTechnician = async (index: number) => {
    const technician = technicians[index];

    if (technician._id) {
      try {
        await deleteData(`${urlEnum.technicians}/${technician._id}`);
        toast.success(Vocabulary.deleteSuccess);
        loadTechnicians();
      } catch (error) {
        toast.error(Vocabulary.error);
        console.error("Error deleting technician:", error);
      }
    } else {
      // Remove unsaved technician from local state
      const newTechnicians = technicians.filter((_, i) => i !== index);
      setTechnicians(newTechnicians);
    }
  };

  const handleChangeTechnician = (
    index: number,
    field: keyof TechnicianModel,
    value: string
  ) => {
    const newTechnicians = [...technicians];
    newTechnicians[index] = {
      ...newTechnicians[index],
      [field]: value,
    };
    setTechnicians(newTechnicians);
  };

  if (!customer._id) {
    return (
      <div style={{ marginTop: 20, padding: 20, textAlign: "center" }}>
        <p>{Vocabulary.saveCustomerFirst || "Please save the partner first before adding technicians."}</p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20 }}>
      {loading && <p>Loading...</p>}
      {technicians.map((technician, index) => (
        <div key={technician._id || `new-${index}`} style={{ marginBottom: 20 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontWeight: "bold" }}>
                  {Vocabulary.technician} #{index + 1}
                  {!technician._id && " (unsaved)"}
                </span>
                <div>
                  <IconButton
                    color="primary"
                    onClick={() => handleSaveTechnician(index)}
                    size="small"
                    title={Vocabulary.save}
                  >
                    <Save />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveTechnician(index)}
                    size="small"
                    title={Vocabulary.delete}
                  >
                    <Delete />
                  </IconButton>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <TextValidator
                size="small"
                id={`technician-name-${index}`}
                name="name"
                label={Vocabulary.name}
                fullWidth
                variant="outlined"
                value={technician.name}
                validators={["required"]}
                errorMessages={[Vocabulary.requiredField]}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeTechnician(index, "name", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <TextValidator
                size="small"
                id={`technician-phone-${index}`}
                name="phone"
                label={Vocabulary.phone}
                fullWidth
                variant="outlined"
                value={technician.phone}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeTechnician(index, "phone", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <TextValidator
                size="small"
                id={`technician-email-${index}`}
                name="email"
                label={Vocabulary.email}
                fullWidth
                variant="outlined"
                value={technician.email}
                validators={technician.email ? ["isEmail"] : []}
                errorMessages={[Vocabulary.emailValidation]}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeTechnician(index, "email", event.target.value)
                }
              />
            </Grid>
          </Grid>
          {index < technicians.length - 1 && (
            <Divider style={{ marginTop: 15 }} />
          )}
        </div>
      ))}
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={handleAddTechnician}
        style={{ marginTop: 10 }}
      >
        {Vocabulary.addTechnician}
      </Button>
    </div>
  );
};

export default Technicians;
